/* ============================================================
   Dathent — Admin Referrals review screen (/app/admin/referrals)
   Gated server-side by admin email allowlist. Lists waitlist
   signups + payout requests, allows marking payouts paid.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, DathentAPIError } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function fmtEur(cents) {
  return `€${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function StatusPill({ status }) {
  const cls = {
    waitlist: 'rr-pill-waitlist',
    activated: 'rr-pill-activated',
    churned: 'rr-pill-churned',
    requested: 'rr-pill-waitlist',
    processing: 'rr-pill-waitlist',
    paid: 'rr-pill-activated',
    cancelled: 'rr-pill-churned',
  }[status] || 'rr-pill-churned';
  return (
    <span className={`pill ${cls}`}>
      <span className="dot" /> {status}
    </span>
  );
}

function Stats({ data }) {
  if (!data) return null;
  const cells = [
    { l: 'Total signups', v: data.total_signups, sub: `${data.waitlist} waitlist · ${data.activated} activated` },
    { l: 'Referrers', v: data.total_referrers, sub: 'with profiles' },
    { l: 'Pending payouts', v: data.pending_payouts, sub: fmtEur(data.pending_amount_cents) },
  ];
  return (
    <div className="rr-stats">
      {cells.map((c) => (
        <div key={c.l} className="card rr-stat">
          <div className="rr-stat-l">{c.l}</div>
          <div className="rr-stat-v">{c.v}</div>
          <div className="rr-stat-sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function SignupsTable({ rows, filter, setFilter, loading }) {
  return (
    <section className="card rr-card">
      <header className="rr-card-h rr-card-h-row">
        <div>
          <div className="eyebrow"><span>Signups</span></div>
          <h2>Waitlist + activated leads.</h2>
        </div>
        <div className="rr-tabs">
          {[
            { id: 'all', label: 'All' },
            { id: 'waitlist', label: 'Waitlist' },
            { id: 'activated', label: 'Activated' },
            { id: 'churned', label: 'Churned' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              className={`rr-tab ${filter === t.id ? 'is-active' : ''}`}
              onClick={() => setFilter(t.id)}
            >{t.label}</button>
          ))}
        </div>
      </header>
      {loading ? (
        <div className="rr-table-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rr-table-empty">No signups yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-subtle)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Email</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Referrer</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Contact</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Locale</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Status</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '10px 6px', wordBreak: 'break-all' }}>{r.email}</td>
                  <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 500 }}>{r.referrerName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>{r.referrerCode}</div>
                  </td>
                  <td style={{ padding: '10px 6px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {r.contactValue ? <>{r.contactValue} <span style={{ color: 'var(--text-subtle)' }}>({r.contactKind})</span></> : '—'}
                  </td>
                  <td style={{ padding: '10px 6px' }}>{(r.locale || 'en').toUpperCase()}</td>
                  <td style={{ padding: '10px 6px' }}><StatusPill status={r.status} /></td>
                  <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PayoutsTable({ rows, filter, setFilter, onMarkPaid, busyId }) {
  return (
    <section className="card rr-card">
      <header className="rr-card-h rr-card-h-row">
        <div>
          <div className="eyebrow"><span>Payouts</span></div>
          <h2>Payout requests + history.</h2>
        </div>
        <div className="rr-tabs">
          {[
            { id: 'all', label: 'All' },
            { id: 'requested', label: 'Requested' },
            { id: 'paid', label: 'Paid' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              className={`rr-tab ${filter === t.id ? 'is-active' : ''}`}
              onClick={() => setFilter(t.id)}
            >{t.label}</button>
          ))}
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="rr-table-empty">No payouts yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-subtle)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Referrer</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Amount</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Method</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Status</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Requested</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Paid</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '10px 6px' }}>
                    <div style={{ fontWeight: 500 }}>{p.referrerName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>{p.referrerCode}</div>
                  </td>
                  <td style={{ padding: '10px 6px', fontWeight: 600 }}>{fmtEur(p.amountCents)} {p.currency}</td>
                  <td style={{ padding: '10px 6px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.method || '—'}</td>
                  <td style={{ padding: '10px 6px' }}><StatusPill status={p.status} /></td>
                  <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(p.requestedAt)}</td>
                  <td style={{ padding: '10px 6px', color: 'var(--text-muted)', fontSize: 12 }}>{fmtDate(p.paidAt)}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right' }}>
                    {p.status === 'requested' && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => onMarkPaid(p)}
                        disabled={busyId === p.id}
                      >
                        {busyId === p.id ? '…' : 'Mark paid'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MarkPaidModal({ payout, onClose, onSubmit }) {
  const [method, setMethod] = useState(payout?.method || 'bank');
  const [details, setDetails] = useState(payout?.methodDetails || '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!payout) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({ method, methodDetails: details, notes });
      onClose();
    } catch (e) {
      setErr(e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-elev)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-xl)', padding: 24, maxWidth: 460, width: '100%',
        }}
      >
        <h3 style={{ margin: '0 0 4px' }}>Mark payout paid</h3>
        <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: 13 }}>
          {payout.referrerName} ({payout.referrerCode}) · {fmtEur(payout.amountCents)} {payout.currency}
        </p>

        <div className="rr-field">
          <label className="rr-l">Method</label>
          <select className="rr-input" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="bank">Bank</option>
            <option value="paypal">PayPal</option>
            <option value="crypto">Crypto</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="rr-field" style={{ marginTop: 12 }}>
          <label className="rr-l">Details</label>
          <input
            className="rr-input"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Last 4, tx hash, etc."
            maxLength={500}
          />
        </div>
        <div className="rr-field" style={{ marginTop: 12 }}>
          <label className="rr-l">Notes</label>
          <textarea
            className="rr-input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
        </div>

        {err && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? '…' : 'Confirm paid'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminReferrals() {
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [signupFilter, setSignupFilter] = useState('all');
  const [payoutFilter, setPayoutFilter] = useState('all');
  const [loadingSignups, setLoadingSignups] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(null);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [s, sg, py] = await Promise.all([
        api.adminReferral.stats(),
        api.adminReferral.listSignups({ status: signupFilter }),
        api.adminReferral.listPayouts({ status: payoutFilter }),
      ]);
      setStats(s.stats || null);
      setSignups(sg.signups || []);
      setPayouts(py.payouts || []);
    } catch (err) {
      if (err instanceof DathentAPIError && (err.status === 401 || err.status === 403)) {
        setForbidden(true);
      } else {
        setError(err?.message || 'Failed to load');
      }
    } finally {
      setLoadingSignups(false);
    }
  }, [signupFilter, payoutFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const onMarkPaid = (payout) => setMarkPaidTarget(payout);
  const submitMarkPaid = async (patch) => {
    setBusyId(markPaidTarget.id);
    try {
      await api.adminReferral.markPayoutPaid(markPaidTarget.id, patch);
      await loadAll();
    } finally {
      setBusyId(null);
    }
  };

  if (forbidden) {
    return (
      <div className="rr-root">
        <div className="rr-setup card" style={{ marginTop: 60 }}>
          <h2>Admin access required</h2>
          <p>Your account is not in the admin allowlist. Ask the operator to add your email.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rr-root">
      <div className="rr-top">
        <div>
          <h1 className="rr-h">Admin · Referrals</h1>
          <p className="rr-sub">Operator review of waitlist signups and payout requests.</p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={loadAll}>
          <I.zap size={13} /> Refresh
        </button>
      </div>

      {error && (
        <div className="rs-error" style={{ marginBottom: 4 }}>{error}</div>
      )}

      <Stats data={stats} />

      <SignupsTable
        rows={signups}
        filter={signupFilter}
        setFilter={setSignupFilter}
        loading={loadingSignups}
      />

      <PayoutsTable
        rows={payouts}
        filter={payoutFilter}
        setFilter={setPayoutFilter}
        onMarkPaid={onMarkPaid}
        busyId={busyId}
      />

      <MarkPaidModal
        payout={markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onSubmit={submitMarkPaid}
      />
    </div>
  );
}

export default AdminReferrals;
